// CommandPalette
//
// Cmd/Ctrl+K → focused overlay search that lets users jump to any matter,
// contact, or app page from anywhere in the app. Keyboard-first:
//
//   ⌘/Ctrl+K   open
//   ↑ / ↓      move selection
//   Enter      navigate to selected
//   Esc        close
//
// Two layers of results:
//   1. Static "navigate to page" commands (Dashboard, Matters, Settings…)
//      that always show and rank by simple prefix match.
//   2. Live database search (matters + contacts + documents) via
//      globalSearch() — debounced 250ms so users don't fire a request on
//      every keystroke.
//
// Designed to be cheap on bad connections — empty query shows only the
// static commands, no network call. Network errors fail silently so the
// static commands keep working even when offline.

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Search, Briefcase, Users, FileText, Home, Calendar, Clock, Receipt,
  ClipboardList, ScrollText, Settings, MessageSquare, Wallet, Activity,
  CornerDownLeft, ArrowUp, ArrowDown,
} from 'lucide-react';
import { useFirm } from '../contexts/FirmContext';
import { useRoute } from '../lib/router';
import { globalSearch } from '../lib/db';

// ── Static nav commands ────────────────────────────────────────────────
const NAV_COMMANDS = [
  { id: 'nav-dashboard',  label: 'Dashboard',              icon: Home,          path: '/',          keywords: ['home','overview'] },
  { id: 'nav-matters',    label: 'Case files',             icon: Briefcase,     path: '/matters',   keywords: ['matters','cases','case files'] },
  { id: 'nav-contacts',   label: 'Contacts',               icon: Users,         path: '/contacts',  keywords: ['clients','people'] },
  { id: 'nav-calendar',   label: 'Calendar',               icon: Calendar,      path: '/calendar',  keywords: ['events','schedule'] },
  { id: 'nav-deadlines',  label: 'Deadlines',              icon: Calendar,      path: '/deadlines', keywords: ['court','dates'] },
  { id: 'nav-tasks',      label: 'Tasks',                  icon: ClipboardList, path: '/tasks',     keywords: ['todo','work'] },
  { id: 'nav-time',       label: 'Time tracking',          icon: Clock,         path: '/time',      keywords: ['hours','billable'] },
  { id: 'nav-billing',    label: 'Billing & Invoices',     icon: Receipt,       path: '/billing',   keywords: ['invoices','money','pay'] },
  { id: 'nav-trust',      label: 'Trust accounts',         icon: Wallet,        path: '/trust',     keywords: ['iolta','escrow','retainer'] },
  { id: 'nav-documents',  label: 'Documents',              icon: FileText,      path: '/documents', keywords: ['files','folders'] },
  { id: 'nav-intake',     label: 'Intake forms',           icon: ScrollText,    path: '/intake',    keywords: ['leads','prospect'] },
  { id: 'nav-messages',   label: 'Messages',               icon: MessageSquare, path: '/messages',  keywords: ['chat','channels'] },
  { id: 'nav-activity',   label: 'Activity log',           icon: Activity,      path: '/activity',  keywords: ['audit','history'] },
  { id: 'nav-employees',  label: 'Employees',              icon: Users,         path: '/employees', keywords: ['team','staff'] },
  { id: 'nav-settings',   label: 'Settings',               icon: Settings,      path: '/settings',  keywords: ['preferences','config'] },
];

// Simple fuzzy: does every word in the query appear (in order or not)
// somewhere in label + keywords?
function matchesNav(cmd, query) {
  if (!query) return true;
  const hay = `${cmd.label} ${(cmd.keywords || []).join(' ')}`.toLowerCase();
  return query.toLowerCase().split(/\s+/).every((tok) => hay.includes(tok));
}

export default function CommandPalette() {
  const { firmId } = useFirm();
  const [, navigate] = useRoute();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [results, setResults] = useState({ matters: [], contacts: [], documents: [] });
  const [searching, setSearching] = useState(false);

  const inputRef = useRef(null);
  const listRef  = useRef(null);

  // ── Global hotkey ─────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      // Cmd+K on Mac, Ctrl+K on Win/Linux
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Reset when closing.
  useEffect(() => {
    if (!open) { setQuery(''); setActive(0); setResults({ matters: [], contacts: [], documents: [] }); return; }
    // Autofocus the input next tick.
    setTimeout(() => inputRef.current?.focus(), 10);
  }, [open]);

  // ── Live search (debounced) ───────────────────────────────────────────
  useEffect(() => {
    if (!open || !firmId || !query || query.length < 2) {
      setResults({ matters: [], contacts: [], documents: [] });
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await globalSearch(firmId, query);
        if (!cancelled) setResults(res || { matters: [], contacts: [], documents: [] });
      } catch (_) {
        // Network error — keep the previous results, fail silently.
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, firmId, query]);

  // ── Flatten everything into one ordered list for keyboard navigation ─
  const allItems = useMemo(() => {
    const navMatches = NAV_COMMANDS.filter((c) => matchesNav(c, query));
    const out = navMatches.map((c) => ({ kind: 'nav', ...c }));
    (results.matters  || []).forEach((m) => out.push({ kind: 'matter', ...m }));
    (results.contacts || []).forEach((c) => out.push({ kind: 'contact', ...c }));
    (results.documents|| []).forEach((d) => out.push({ kind: 'document', ...d }));
    return out;
  }, [query, results]);

  // Keep active index in bounds whenever the list shrinks.
  useEffect(() => {
    setActive((i) => Math.min(i, Math.max(0, allItems.length - 1)));
  }, [allItems.length]);

  // Scroll active item into view.
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${active}"]`);
    if (el?.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const go = useCallback((item) => {
    if (!item) return;
    if (item.kind === 'nav')      navigate(item.path);
    if (item.kind === 'matter')   navigate(`/matters/${item.id}`);
    if (item.kind === 'contact')  navigate(`/contacts/${item.id}`);
    if (item.kind === 'document') navigate(item.matter_id ? `/matters/${item.matter_id}` : '/documents');
    setOpen(false);
  }, [navigate]);

  const onInputKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, allItems.length - 1)); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter')     { e.preventDefault(); go(allItems[active]); }
  };

  if (!open) return null;

  // Compute section start indices for header rendering.
  let cursor = 0;
  const sections = [];
  const navMatches = NAV_COMMANDS.filter((c) => matchesNav(c, query));
  if (navMatches.length)         { sections.push({ title: 'Go to',     items: navMatches,        start: cursor }); cursor += navMatches.length; }
  if (results.matters?.length)   { sections.push({ title: 'Matters',   items: results.matters,   start: cursor }); cursor += results.matters.length; }
  if (results.contacts?.length)  { sections.push({ title: 'Contacts',  items: results.contacts,  start: cursor }); cursor += results.contacts.length; }
  if (results.documents?.length) { sections.push({ title: 'Documents', items: results.documents, start: cursor }); cursor += results.documents.length; }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] bg-slate-900/50 backdrop-blur-sm px-4"
      onClick={() => setOpen(false)}
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      <div
        className="w-full max-w-xl rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-800">
          <Search size={16} className="text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActive(0); }}
            onKeyDown={onInputKey}
            placeholder="Search matters, contacts, documents, or jump to a page…"
            className="flex-1 bg-transparent outline-none text-sm text-slate-900 dark:text-white placeholder-slate-400"
          />
          {searching && (
            <span className="text-xs text-slate-400">Searching…</span>
          )}
          <kbd className="hidden sm:inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-mono">ESC</kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto py-1">
          {allItems.length === 0 ? (
            <div className="text-center text-sm text-slate-400 py-12">
              {query ? 'No matches found.' : 'Start typing to search.'}
            </div>
          ) : (
            sections.map((section) => (
              <div key={section.title} className="py-1">
                <p className="px-4 py-1 text-[10px] uppercase tracking-wider font-semibold text-slate-400 dark:text-slate-500">
                  {section.title}
                </p>
                {section.items.map((item, i) => {
                  const idx = section.start + i;
                  const isActive = idx === active;
                  return (
                    <Row
                      key={`${section.title}-${item.id}`}
                      idx={idx}
                      isActive={isActive}
                      onClick={() => go({ kind: kindFor(section.title), ...item })}
                      onMouseEnter={() => setActive(idx)}
                      item={item}
                      kind={kindFor(section.title)}
                    />
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div className="px-4 py-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-[11px] text-slate-400 dark:text-slate-500">
          <span className="flex items-center gap-2">
            <kbd className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800"><ArrowUp size={10} /><ArrowDown size={10} /></kbd>
            navigate
            <kbd className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800"><CornerDownLeft size={10} /></kbd>
            open
          </span>
          <span>{allItems.length} result{allItems.length === 1 ? '' : 's'}</span>
        </div>
      </div>
    </div>
  );
}

function kindFor(title) {
  if (title === 'Go to')     return 'nav';
  if (title === 'Matters')   return 'matter';
  if (title === 'Contacts')  return 'contact';
  if (title === 'Documents') return 'document';
  return 'nav';
}

const Row = ({ item, kind, idx, isActive, onClick, onMouseEnter }) => {
  const Icon = item.icon || (kind === 'matter' ? Briefcase : kind === 'contact' ? Users : kind === 'document' ? FileText : Home);

  let primary, secondary;
  if (kind === 'nav') {
    primary   = item.label;
    secondary = item.path;
  } else if (kind === 'matter') {
    primary   = item.title;
    const client = item.client
      ? [item.client.first_name, item.client.last_name].filter(Boolean).join(' ') || item.client.organization
      : null;
    secondary = [item.matter_number, client, item.practice_area].filter(Boolean).join(' · ');
  } else if (kind === 'contact') {
    primary   = [item.first_name, item.last_name].filter(Boolean).join(' ') || item.organization || item.email;
    secondary = [item.email, item.contact_type].filter(Boolean).join(' · ');
  } else {
    primary   = item.name;
    secondary = item.category || 'Document';
  }

  return (
    <button
      type="button"
      data-idx={idx}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={[
        'w-full flex items-center gap-3 px-4 py-2.5 text-left transition',
        isActive
          ? 'bg-indigo-50 dark:bg-indigo-900/30'
          : 'hover:bg-slate-50 dark:hover:bg-slate-800',
      ].join(' ')}
    >
      <Icon size={15} className={isActive ? 'text-indigo-600 dark:text-indigo-300' : 'text-slate-400'} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{primary}</p>
        {secondary && <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{secondary}</p>}
      </div>
    </button>
  );
};
